import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Play, Pause, Square, Volume2 } from 'lucide-react';

export const SpeechHighlighter = ({ text, className = "", style = {} }) => {
    const [isPaused, setIsPaused] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [currentWordIndex, setCurrentWordIndex] = useState(-1);
    
    const utteranceRef = useRef(null);
    const wordIndexRef = useRef(0);
    const globalWordsRef = useRef([]);
    const startIndexRef = useRef(0);
    const panelRef = useRef(null);

    // Split text into words and prepare for rendering
    const words = text ? text.split(/(\s+)/).filter(word => word.trim() !== '') : [];

    useEffect(() => {
        globalWordsRef.current = words;
        
        // Initialize speech synthesis
        const utterance = new SpeechSynthesisUtterance();
        utteranceRef.current = utterance;
        utterance.lang = 'en-GB';
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;

        // Function to set the desired voice
        const setVoice = () => {
            const voices = speechSynthesis.getVoices();
            const desiredVoice = voices.find(voice => 
                voice.name.includes("Microsoft George") || 
                voice.name.includes("Google UK English Male") ||
                voice.lang === 'en-GB'
            );
            if (desiredVoice) {
                utterance.voice = desiredVoice;
            }
        };

        // Ensure voices are loaded
        if (speechSynthesis.onvoiceschanged !== undefined) {
            speechSynthesis.onvoiceschanged = setVoice;
        }
        setVoice();

        // Cleanup function
        return () => {
            if (speechSynthesis.speaking) {
                speechSynthesis.cancel();
            }
        };
    }, [text]);

    const resetHighlighting = useCallback(() => {
        setCurrentWordIndex(-1);
    }, []);

    const setupUtteranceCallbacks = useCallback((utterance, startFromIndex) => {
        let localWordIndex = 0;
        
        utterance.onboundary = function(event) {
            if (event.name === 'word') {
                const absoluteWordIndex = startFromIndex + localWordIndex;
                
                if (absoluteWordIndex < globalWordsRef.current.length) {
                    setCurrentWordIndex(absoluteWordIndex);
                    localWordIndex++;
                }
            }
        };

        utterance.onend = function() {
            setIsSpeaking(false);
            setIsPaused(false);
            resetHighlighting();
            wordIndexRef.current = 0;
            startIndexRef.current = 0;
        };

        utterance.onpause = function() {
            setIsPaused(true);
            setIsSpeaking(false);
        };

        utterance.onresume = function() {
            setIsPaused(false);
            setIsSpeaking(true);
        };

        utterance.onerror = function(event) {
            console.error('Speech synthesis error:', event);
            setIsSpeaking(false);
            setIsPaused(false);
            resetHighlighting();
        };
    }, [resetHighlighting]);

    const handlePlay = useCallback(() => {
        if (!text) return;

        if (isPaused) {
            speechSynthesis.resume();
            setIsPaused(false);
            return;
        }

        // Reset if starting fresh
        wordIndexRef.current = 0;
        startIndexRef.current = 0;
        resetHighlighting();
        
        const utterance = utteranceRef.current;
        utterance.text = text;
        
        setupUtteranceCallbacks(utterance, 0);
        
        speechSynthesis.speak(utterance);
        setIsSpeaking(true);
    }, [text, isPaused, resetHighlighting, setupUtteranceCallbacks]);

    const handlePause = useCallback(() => {
        if (speechSynthesis.speaking && !isPaused) {
            speechSynthesis.pause();
        }
    }, [isPaused]);

    const handleStop = useCallback(() => {
        speechSynthesis.cancel();
        setIsSpeaking(false);
        setIsPaused(false);
        resetHighlighting();
        wordIndexRef.current = 0;
        startIndexRef.current = 0;
    }, [resetHighlighting]);

    const handleWordClick = useCallback((index) => {
        if (!text) return;

        // Stop current speech
        speechSynthesis.cancel();
        
        // Reset highlighting
        resetHighlighting();
        
        // Start from clicked word
        wordIndexRef.current = 0;
        startIndexRef.current = index;
        
        const remainingText = globalWordsRef.current.slice(index).join(' ');
        const utterance = utteranceRef.current;
        utterance.text = remainingText;
        
        // Set up callbacks with the new starting index
        setupUtteranceCallbacks(utterance, index);
        
        speechSynthesis.speak(utterance);
        setIsSpeaking(true);
        setIsPaused(false);
    }, [text, resetHighlighting, setupUtteranceCallbacks]);

    const renderText = () => {
        return words.map((word, index) => (
            <span
                key={index}
                onClick={() => handleWordClick(index)}
                style={{
                    cursor: 'pointer',
                    padding: '2px 1px',
                    borderRadius: '3px',
                    transition: 'all 0.2s ease',
                    backgroundColor: currentWordIndex === index ? '#3b82f6' : 'transparent',
                    color: currentWordIndex === index ? 'white' : 'inherit',
                    fontWeight: currentWordIndex === index ? '600' : 'normal',
                    display: 'inline', // Ensure inline display
                    wordBreak: 'break-word', // Allow word breaking if needed
                }}
                onMouseEnter={(e) => {
                    if (currentWordIndex !== index) {
                        e.target.style.backgroundColor = '#f3f4f6';
                    }
                }}
                onMouseLeave={(e) => {
                    if (currentWordIndex !== index) {
                        e.target.style.backgroundColor = 'transparent';
                    }
                }}
            >
                {word}
            </span>
        ));
    };

    if (!text) {
        return (
            <div className={className} style={style}>
                <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                    No text provided for speech synthesis.
                </p>
            </div>
        );
    }

    return (
        <div className={className} style={style}>
            {/* Control Panel */}
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                flexWrap: 'wrap' // Allow controls to wrap on smaller screens
            }}>
                <Volume2 size={20} style={{ color: '#64748b' }} />
                <span style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#475569',
                    marginRight: '12px'
                }}>
                    Text-to-Speech:
                </span>
                
                <button
                    onClick={handlePlay}
                    disabled={isSpeaking && !isPaused}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        fontSize: '14px',
                        backgroundColor: (isSpeaking && !isPaused) ? '#d1d5db' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (isSpeaking && !isPaused) ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s'
                    }}
                >
                    <Play size={16} />
                    {isPaused ? 'Resume' : 'Play'}
                </button>
                
                <button
                    onClick={handlePause}
                    disabled={!isSpeaking || isPaused}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        fontSize: '14px',
                        backgroundColor: (!isSpeaking || isPaused) ? '#d1d5db' : '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (!isSpeaking || isPaused) ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s'
                    }}
                >
                    <Pause size={16} />
                    Pause
                </button>
                
                <button
                    onClick={handleStop}
                    disabled={!isSpeaking && !isPaused}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        fontSize: '14px',
                        backgroundColor: (!isSpeaking && !isPaused) ? '#d1d5db' : '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (!isSpeaking && !isPaused) ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s'
                    }}
                >
                    <Square size={16} />
                    Stop
                </button>

                {isSpeaking && (
                    <span style={{ 
                        fontSize: '12px', 
                        color: '#10b981',
                        fontWeight: '500',
                        marginLeft: '12px'
                    }}>
                        ● Speaking...
                    </span>
                )}
            </div>

            {/* Text Display */}
            <div 
                ref={panelRef}
                style={{
                    padding: '20px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    lineHeight: '1.8',
                    fontSize: '16px',
                    color: '#374151',
                    userSelect: 'none',
                    // Key fixes for text wrapping:
                    wordWrap: 'break-word', // Break long words if necessary
                    overflowWrap: 'break-word', // Modern alternative to word-wrap
                    whiteSpace: 'normal', // Allow normal text wrapping
                    overflow: 'hidden', // Hide any overflow
                    width: '100%', // Ensure container takes full width
                    boxSizing: 'border-box', // Include padding in width calculation
                    maxWidth: '100%' // Prevent exceeding parent width
                }}
            >
                {renderText()}
            </div>

            {/* Instructions */}
            <div style={{ 
                marginTop: '12px', 
                fontSize: '13px', 
                color: '#6b7280',
                fontStyle: 'italic'
            }}>
                💡 Click on any word to start reading from that point
            </div>
        </div>
    );
};

// Demo component to test the speech highlighter
export default function SpeechHighlighterDemo() {
    const sampleText = "This is a sample text to demonstrate the speech highlighter functionality. The text should wrap properly within the container and each word should be clickable to start reading from that point. Long words should break appropriately to prevent overflow from the container boundaries.";
    
    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ marginBottom: '20px', color: '#1f2937' }}>Speech Highlighter Demo</h1>
            <SpeechHighlighter text={sampleText} />
        </div>
    );
}